import React from "react";
import dayjs from "dayjs";


/**
 * Usage variants:
 * <ProductPrices product={product} />
 * or
 * <ProductPrices regularPrice={19.99} salePrice={13.99} fromDate="..." toDate="..." />
 */
// function ProductListPrice(props) {
//     // support both shapes
//     const product = props.product ?? {};
//     const regularPriceRaw = props.regularPrice ?? product.price ?? product.listPrice ?? product.regularPrice;
//     const salePriceRaw = props.salePrice ?? product.salePrice ?? product.discountPrice ?? product.sale_price;
//     const fromDateRaw = props.fromDate ?? product.fromDate ?? product.from_date;
//     const toDateRaw = props.toDate ?? product.toDate ?? product.to_date;

//     // coerce to numbers if strings
//     const regularPrice = typeof regularPriceRaw === "string" ? Number(regularPriceRaw) : regularPriceRaw;
//     const salePrice = typeof salePriceRaw === "string" ? Number(salePriceRaw) : salePriceRaw;

//     const hasSale = salePrice != null && !Number.isNaN(salePrice) && Number(salePrice) > 0;

//     // parse dates with dayjs; handle missing dates
//     const now = dayjs();
//     const start = fromDateRaw ? dayjs(fromDateRaw).startOf("day") : null;
//     const end = toDateRaw ? dayjs(toDateRaw).endOf("day") : null;


//     // inclusive check using day boundaries to avoid timezone issues
//     const isSaleActive = hasSale && (
//         (start && end && (now.isSame(start) || now.isSame(end) || (now.isAfter(start) && now.isBefore(end)))) ||
//         (start && !end && (now.isSame(start) || now.isAfter(start))) ||
//         (!start && end && (now.isSame(end) || now.isBefore(end)))
//     );

//     // DEBUG: log the computed values (remove in production)
//     // Open browser console to see these values if price isn't showing.
//     // eslint-disable-next-line no-console
//     console.debug("ProductPrices debug:",
//         { regularPriceRaw, salePriceRaw, regularPrice, salePrice, fromDateRaw, toDateRaw, start: start?.toString(), end: end?.toString(), now: now.toString(), isSaleActive });

//     return (
//         <div className="ProductPrices sale prod-collection">
//             {isSaleActive ? (
//                 <>
//                     <h4 className="regular oldprice"><del>{regularPrice?.toFixed(2)}€</del></h4>
//                     <h4 className="regular salening">{salePrice?.toFixed(2)}€</h4>
//                 </>
//             ) : (
//                 regularPrice > 0 ? (
//                     <h4 className="regular">{regularPrice?.toFixed(2)}€</h4>
//                 ) : (
//                     <h4 className="regular">Sold Out</h4>
//                 )
//             )}
//         </div>
//     );
// }


function normalizeDate(v) {
    // Handles: "2025-11-17", "2025-11-17T00:00:00.000Z", or { $date: "..." }
    if (!v) return null;
    if (typeof v === "object" && v.$date) return v.$date;
    return v;
}

function findSkuFromProduct(product, { skuId, variantIndex, sizeIndex } = {}) {
    if (!product) return null;

    // Try skuId first (most explicit)
    if (skuId) {
        for (const variant of product.variants || []) {
            for (const size of variant.sizes || []) {
                const found = (size.skus || []).find(s => s.skuId === skuId || s._id === skuId);
                if (found) return found;
            }
        }
    }

    // Try variantIndex/sizeIndex if provided
    if (typeof variantIndex === "number") {
        const variant = product.variants?.[variantIndex];
        if (variant && typeof sizeIndex === "number") {
            return variant.sizes?.[sizeIndex]?.skus?.[0] ?? null;
        }
        // fallback to first sku of variant
        return variant?.sizes?.[0]?.skus?.[0] ?? null;
    }

    // Default fallback: first SKU with quantity > 0
    for (const variant of product.variants || []) {
        for (const size of variant.sizes || []) {
            const sku = (size.skus || []).find(s => (s.quantity ?? 0) > 0);
            if (sku) return sku;
        }
    }

    // Final fallback: just return first sku anywhere
    for (const variant of product.variants || []) {
        for (const size of variant.sizes || []) {
            if ((size.skus || []).length) return size.skus[0];
        }
    }

    return null;
}

export default function ProductListPrice(props) {
    // allow passing explicit sku or product
    const product = props.product ?? {};
    const skuProp = props.sku ?? props.skuId ?? null;

    // If user passed a SKU object directly
    const explicitSkuObject = skuProp && typeof skuProp === "object" ? skuProp : null;
    const sku = explicitSkuObject || findSkuFromProduct(product, { skuId: skuProp, variantIndex: props.variantIndex, sizeIndex: props.sizeIndex });

    // if you still want to support old props shape:
    const regularPriceRaw = props.regularPrice ?? sku?.listPrice ?? sku?.price ?? product.price ?? product.listPrice ?? product.regularPrice;
    const salePriceRaw = props.salePrice ?? sku?.discountPrice ?? product.salePrice ?? product.discountPrice ?? product.sale_price;
    const salePrice2Raw = props.salePrice2 ?? sku?.discountPrice2 ?? product.discountPrice2;
    const fromDateRaw = props.fromDate ?? normalizeDate(sku?.fromDate) ?? normalizeDate(product.fromDate);
    const toDateRaw = props.toDate ?? normalizeDate(sku?.toDate) ?? normalizeDate(product.toDate);
    const fromDate2Raw = props.fromDate2 ?? normalizeDate(sku?.fromDate2) ?? normalizeDate(product.fromDate2) ?? fromDateRaw;
    const toDate2Raw = props.toDate2 ?? normalizeDate(sku?.toDate2) ?? normalizeDate(product.toDate2) ?? toDateRaw;

    const regularPrice = typeof regularPriceRaw === "string" ? Number(regularPriceRaw) : regularPriceRaw;
    const salePrice = typeof salePriceRaw === "string" ? Number(salePriceRaw) : salePriceRaw;
    const salePrice2 = typeof salePrice2Raw === "string" ? Number(salePrice2Raw) : salePrice2Raw;

    const hasSale = salePrice != null && !Number.isNaN(salePrice) && Number(salePrice) > 0;
    const hasSale2 = salePrice2 != null && !Number.isNaN(salePrice2) && Number(salePrice2) > 0;
    const now = dayjs();
    const start = fromDateRaw ? dayjs(fromDateRaw).startOf("day") : null;
    const end = toDateRaw ? dayjs(toDateRaw).endOf("day") : null;
    const start2 = fromDate2Raw ? dayjs(fromDate2Raw).startOf("day") : null;
    const end2 = toDate2Raw ? dayjs(toDate2Raw).endOf("day") : null;

    const isSaleActive = hasSale && (
        (start && end && (now.isSame(start) || now.isSame(end) || (now.isAfter(start) && now.isBefore(end)))) ||
        (start && !end && (now.isSame(start) || now.isAfter(start))) ||
        (!start && end && (now.isSame(end) || now.isBefore(end)))
    );

    const isSale2Active = hasSale2 && (
        (start2 && end2 && (now.isSame(start2) || now.isSame(end2) || (now.isAfter(start2) && now.isBefore(end2)))) ||
        (start2 && !end2 && (now.isSame(start2) || now.isAfter(start2))) ||
        (!start2 && end2 && (now.isSame(end2) || now.isBefore(end2)))
    );

    // DEBUG (remove in production)
    console.debug("ProductPrices debug:", { sku, regularPriceRaw, salePriceRaw, salePrice2Raw, regularPrice, salePrice, salePrice2, fromDateRaw, toDateRaw, fromDate2Raw, toDate2Raw, start: start?.toString(), end: end?.toString(), start2: start2?.toString(), end2: end2?.toString(), now: now.toString(), isSaleActive, isSale2Active });

    return (
        <div className={`ProductPrices sale prod-collection ${(isSaleActive || isSale2Active) ? "on-sale" : ""}`}>
            {isSale2Active ? (
                <>
                    <div className="price-row">
                        <h4 className="regular oldprice"><del>{Number(regularPrice).toFixed(2)}€</del></h4>
                        {hasSale && <h4 className="regular oldprice mx-1"><del>{Number(salePrice).toFixed(2)}€</del></h4>}
                        <h4 className="regular salening">{Number(salePrice2).toFixed(2)}€</h4>
                        {/* Badge removed - DiscountBadge component handles badges */}
                    </div>
                </>
            ) : isSaleActive ? (
                <>
                    <div className="price-row">
                        <h4 className="regular oldprice"><del>{Number(regularPrice).toFixed(2)}€</del></h4>
                        <h4 className="regular salening">{Number(salePrice).toFixed(2)}€</h4>
                        {/* Badge removed - DiscountBadge component handles badges */}
                    </div>
                </>
            ) : (
                regularPrice > 0 ? (
                    <h4 className="regular">{Number(regularPrice).toFixed(2)}€</h4>
                ) : (
                    <h4 className="regular">Sold Out</h4>
                )
            )}
        </div>
    );
}


